/**
 * "What's new" announcements and one-time tips. The server decides what a person is offered (by their permissions
 * and the business's plan) and remembers what they dismissed; this only draws it. It is optional UI: nothing here
 * ever shows an error or blocks the page.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { portal } from "./api";
import type { WhatsNewFeed, WhatsNewItem } from "./types";

/** How long "Snooze" hides an item for. */
export const SNOOZE_DAYS = 7;

export const whatsNewKey = (orgId: string) => ["whats-new", orgId] as const;

/** What a mark changes; a field left out stays as it is. */
export interface MarkChange {
  seen?: boolean;
  dismissed?: boolean;
  /** Hide until then (timezone-aware ISO); `null` clears a snooze. */
  snooze_until?: string | null;
}

/** The feed as it will be once `ids` carry `change`: what to show before the server has answered. */
export function applyMarks(feed: WhatsNewFeed, ids: readonly string[], change: MarkChange): WhatsNewFeed {
  const marked = new Set(ids);
  const hides =
    change.dismissed === true || (change.snooze_until !== undefined && change.snooze_until !== null);
  const items = feed.items
    .filter((i) => !(hides && marked.has(i.id)))
    .map((i) => (change.seen === true && marked.has(i.id) ? { ...i, is_new: false } : i));
  return { items, new_count: items.filter((i) => i.kind === "news" && i.is_new).length };
}

/** An ISO moment `days` from `now`, for a snooze. */
export function snoozeUntil(days: number, now: Date = new Date()): string {
  return new Date(now.getTime() + days * 86_400_000).toISOString();
}

/** Newest first, whatever order the server sent. */
export const newestFirst = (items: readonly WhatsNewItem[]): WhatsNewItem[] =>
  [...items].sort((a, b) => b.published_at.localeCompare(a.published_at));

/** The current business's announcements and tips. Shared by the header menu and every tip, so one request. */
export function useWhatsNew() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: whatsNewKey(orgId),
    queryFn: () => portal.whatsNew(orgId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Marks items seen, dismissed or snoozed. The page updates at once and goes back if the server refuses; either way
 * it then asks the server what is true, so a mark that was lost is never silently shown as saved.
 */
export function useMarkWhatsNew() {
  const qc = useQueryClient();
  const { orgId } = useOrganization();
  const key = whatsNewKey(orgId);
  return useMutation({
    mutationFn: ({ ids, ...change }: { ids: string[] } & MarkChange) =>
      portal.markWhatsNew(orgId, { ids, ...change }),
    onMutate: async ({ ids, ...change }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WhatsNewFeed>(key);
      if (previous) qc.setQueryData(key, applyMarks(previous, ids, change));
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
